import { LogicalOperatorEnum } from '@flowwai-frontend/shared/filters'

import {
   AppealStatusEnum,
   BoardParentType,
   BoardTypeEnum,
   DealInitiatedEntity,
   GOFCategory,
   PipelineEntityTypeEnum
} from '../constants'
import {
   AnalyticsDataSourceTypeEnum,
   AnalyticsLevelEnum,
   BarGraphLayoutEnum,
   FunnelDropValueComparisonTypeEnum,
   FunnelLabelTypeEnum,
   GraphDataLabelsEnum,
   GraphTypeEnum,
   ScorecardComparisonTypeEnum,
   SortValueEnum
} from '../constants/AnalyticsConstants'
import { SystemFieldEnum } from '../constants/fields'
import {
   CONDITION_OPERATORS,
   DATE_CONDITION_OPERATORS,
   NUMBER_CONDITION_OPERATORS,
   PHONE_NUMBER_CONDITION_OPERATORS,
   SELECTOR_CONDITION_OPERATORS,
   TEXT_CONDITION_OPERATORS,
   TIME_CONDITION_OPERATORS
} from '../constants/FilterConstants'
import { SortValueType } from '../constants/SortConstants'
import {
   TaskTemplateTypeEnum,
   TemplateDetailsStatusEnum
} from '../constants/template'
import { ViewLayouts, ViewsParentEntityType } from '../constants/ViewsConstants'
import ActivityTemplateModel from '../models/ActivityTemplateModel'
import ContactTemplateModel from '../models/ContactTemplateModel'
import CustomObjectTemplateModel from '../models/CustomObjectTemplateModel'
import DealTemplateModel from '../models/DealTemplateModel'
import CheckboxGroupFieldModel from '../models/FieldModel/FieldModels/CheckboxGroupFieldModel'
import DateFieldModel from '../models/FieldModel/FieldModels/DateFieldModel'
import DateTimeFieldModel from '../models/FieldModel/FieldModels/DateTimeFieldModel'
import DropdownFieldModel from '../models/FieldModel/FieldModels/DropdownFieldModel'
import EmailFieldModel from '../models/FieldModel/FieldModels/EmailFieldModel'
import FieldSelectorFieldModel from '../models/FieldModel/FieldModels/FieldSelectorFieldModel'
import FloatFieldModel from '../models/FieldModel/FieldModels/FloatFieldModel'
import IntegerFieldModel from '../models/FieldModel/FieldModels/IntegerFieldModel'
import LongTextFieldModel from '../models/FieldModel/FieldModels/LongTextFieldModel'
import MultiSelectFieldModel from '../models/FieldModel/FieldModels/MultiSelectFieldModel'
import PlainTextFieldModel from '../models/FieldModel/FieldModels/PlainTextFieldModel'
import RadioGroupFieldModel from '../models/FieldModel/FieldModels/RadioGroupFieldModel'
import SearchableFieldModel from '../models/FieldModel/FieldModels/SearchableFieldModel'
import TimeFieldModel from '../models/FieldModel/FieldModels/TimeFieldModel'
import UrlFieldModel from '../models/FieldModel/FieldModels/UrlFieldModel'
import DateConditionModel from '../models/filters/DateConditionModel'
import FilterSetModel from '../models/filters/FilterSetModel'
import NumberConditionModel from '../models/filters/NumberConditionModel'
import PhoneNumberConditionModel from '../models/filters/PhoneNumberConditionModel'
import SelectorConditionModel from '../models/filters/SelectorConditionModel'
import TextConditionModel from '../models/filters/TextConditionModel'
import TimeConditionModel from '../models/filters/TimeConditionModel'
import LeadTemplateModel from '../models/LeadTemplateModel'
import TaskTemplateModel from '../models/TaskTemplateModel'
import GroupModel from '../models/GroupModel'
import FilterSetModelV1 from '../models/filters/FilterSetModelV1'
import { UserModel } from '../models/UserModel'
import UserPropertiesTemplateModel from '../models/UserPropertiesTemplateModel'
import { BarGraphModel } from '../store/models/BarGraphModel/BarGraphModel'
import { DonutGraphModel } from '../store/models/DonutGraphModel/DonutGraphModel'
import { MetricModel } from '../store/models/MetricModel/MetricModel'
import { PivotTableGraphModel } from '../store/models/PivotTableGraphModel/PivotTableGraphModel'
import { QuickFiltersModel } from '../store/models/QuickFiltersModel/QuickFiltersModel'
import { ScorecardChartModel } from '../store/models/ScorecardChartModel/ScorecardChartModel'
import { TableGraphModel } from '../store/models/TableGraphModel/TableGraphModel'
import TemplateStore from '../store/TemplateStore'
import { FunnelGraphModel } from '../store/models/FunnelGraphModel/FunnelGraphModel'
import { LineGraphModel } from '../store/models/LineGraphModel/LineGraphModel'
import PipelineEventTemplateModel from '../models/PipelineEventTemplateModel'
import ApplicationTemplateModel from '../models/ApplicationTemplateModel'
import {
   UnionField,
   UnionFieldResponse,
   UnionFieldResponseWithField,
   UnionFieldType
} from './Field'
import { AssigneeFieldMapping, PhoneNumberResponse } from './Field/field'
import { UserAttendance } from './user'
import {
   FeePaymentPermissions,
   StageResponse,
   StageTasksIdentifications
} from './Field/dropdownFields'

export interface ActivityType extends Record {
   title?: string
}

export interface ActivityTemplateType extends Template {
   description?: string
   canTriggerAutomationWorkflows?: boolean
}

export interface RemarkAddedByType {
   userId: string

   name?: string
   profilePicUrl?: string
   attendance?: UserAttendance | null
   designation?: string | null
}

export interface AttachmentType {
   attachmentId: string
   name?: string | null
   mimeType?: string | null
   sizeInBytes?: number | null
   url: string
   preSignedUrl: string
   preSignedDownloadUrl?: string | null
}

export interface RemarkType {
   remarkId: string
   remarkContent?: string | null

   addedAt?: string
   addedBy?: RemarkAddedByType
   lastUpdatedAt?: string | null
   attachments: AttachmentType[]
}

export enum RemarkPositionEnum {
   First = 'FIRST',
   Last = 'LAST'
}

export enum TemplateApplicationTypeEnum {
   Tdr = 'TDR',
   Default = 'DEFAULT'
}

export interface ContactType extends Record {
   contactId: string
}

export enum PipelineItemTypeEnum {
   lead = 'LEAD',
   deal = 'DEAL',
   contact = 'CONTACT',
   customObject = 'CUSTOM_OBJECT',
   application = 'APPLICATION'
}
export interface PipelineType {
   pipelineId: string
   name: string
   displayName: string
   pipelineItemTemplateId: string
   taskTemplateIds?: string[]
   activityTemplateIds?: string[]
   pipelineEventTemplateIds?: string[]
   pipelineItemType: PipelineItemTypeEnum
   pipelineIcon: PipelineIcon
   imageUrl?: string
   description?: string
   stageFieldId?: string
   displayStageFieldId?: string
   stageTransitionView?: PipelineStageTransitionStyleEnum
}

export interface ContactTemplateType extends Template {
   assigneeFieldsMapping: AssigneeFieldMapping[]
   identificationFieldIds: Array<string>
}

export interface PipelineEventTemplateType extends Template {}

export interface LeadType extends Record {
   leadId: string
   contact: ContactType
}

export interface CustomObjectType extends Record {
   customObjectId: string
}

export interface ApplicationType extends Record {
   applicationId: string
}

export interface DealType extends Record {
   dealId: string
   contact: ContactType
}

export interface CustomObjectTemplateProperties {
   pluralName: string
   primaryDisplayNameFieldId: string
   singularName: string
}

interface ApplicationSubStatusConfigType {
   fieldIds: Array<string>
   isSubStatusEnabled?: boolean
}

export interface ApplicationTemplateProperties {
   pluralName: string
   primaryDisplayNameFieldId: string
   singularName: string
   subStatusConfig?: ApplicationSubStatusConfigType
}

export type ApplicationCustomTabType = 'NESTED_TAB' | 'RELATION'

export interface TabIcon {
   iconId: string
   iconColor: string
}

export interface ApplicationTemplateTabsType {
   tabId: string
   name: string
   orderedGofIds: string[]
   category: string
   tabEnum: 'ICON' | 'PROGRESS'
   tabIcon?: TabIcon
}

export interface ApplicationCustomNestedTabType {
   tabType: 'NESTED_TAB'
   orderedChildTabs: ApplicationCustomTabsType[]
}

export interface ApplicationCustomRelationTabType {
   tabType: 'RELATION'
   relationId: string
   relationTarget: 'FROM' | 'TO' | null | undefined
}

export interface ApplicationCustomTDRRequestTabType {
   tabType: 'TDR_REQUEST'
   availTdrCreditsFieldId: string
   authorityFieldId: string
}

export interface IconConfig {
   iconId: string
   color: string
}

export enum PipelineTypeEnum {
   Officer = 'OFFICER',
   LineDepartment = 'LINE_DEPARTMENT',
   Na = 'NA'
}

export interface PipelineInProposalFlowTab {
   pipelineId: string
   pipelineName: string
   pipelineType: PipelineTypeEnum
}

export interface ProposalFlowTabConfig {
   name: string
   order: number
   pipelines: Array<PipelineInProposalFlowTab>
   icon?: IconConfig
}

export interface ApplicationCustomProposalFlowTabType {
   tabType: 'PROPOSAL_FLOW'
   proposalFlowTabs: ProposalFlowTabConfig[]
}

export interface ApplicationCustomAppealForumTabType {
   tabType: 'APPEAL_FORUM'
}

export type ApplicationCustomTabDataType =
   | ApplicationCustomNestedTabType
   | ApplicationCustomRelationTabType
   | ApplicationCustomTDRRequestTabType
   | ApplicationCustomProposalFlowTabType
   | ApplicationCustomAppealForumTabType

export interface ApplicationCustomTabsType {
   tabId: string
   name: string
   data: ApplicationCustomTabDataType
   tabIcon?: TabIcon
}

export interface DealInitiatedOnType {
   entityId: string
   entityType: DealInitiatedEntity
}

export interface LeadTemplateType extends Template {
   assigneeFieldsMapping: AssigneeFieldMapping[]
}

export interface CustomObjectTemplateType extends Template {
   assigneeFieldsMapping: AssigneeFieldMapping[]
   customObjectTemplateProperties: CustomObjectTemplateProperties
}

export interface ApplicationTemplateType extends Template {
   assigneeFieldsMapping: AssigneeFieldMapping[]
   applicationTemplateProperties: ApplicationTemplateProperties
   templateApplicationType: TemplateApplicationTypeEnum
   applicationTabs?: ApplicationTemplateTabsType[]
   applicationCustomTabs?: ApplicationCustomTabsType[]
   overviewFieldIds?: string[]
   scrutinyProcessVisibleStageIds?: Array<string>
   proposalFlowVisibleStageIds?: Array<string>
   tdrRequestsVisibleStageIds?: Array<string>
   slaFieldIds?: string[]
   deemedApprovalFieldsConfig?: DeemedApprovalFieldsConfig | null
}

export interface DaApplicabilityFieldConfig {
   fieldId: string
   eligibleResponse: string
   notEligibleResponse: string
}

export interface DaTimestampFieldConfig {
   fieldId: string
}

export interface OfficerCheckStatusFieldConfig {
   fieldId: string
   checkedResponse: string
   notCheckedResponse: string
}

export interface DaStatusFieldConfig {
   fieldId: string
   appliedResponse: string
   autoApprovedResponse: string
}

export interface DeemedApprovalFieldsConfig {
   daApplicabilityField: DaApplicabilityFieldConfig
   daTimestampField: DaTimestampFieldConfig
   officerCheckStatusField: OfficerCheckStatusFieldConfig
   daStatusField: DaStatusFieldConfig
}

export interface DealTemplateType extends Template {
   assigneeFieldsMapping: AssigneeFieldMapping[]
}

export interface TaskType extends Record {
   title: string
}

export interface PipelineItemQuickEditFieldsConfig {
   fieldIds: string[]
   showPipelineItemStageQuickEditFields: boolean
}

export interface TaskTemplateType extends Template {
   color?: string
   description?: string
   taskTemplateType?: TaskTemplateTypeEnum
   pipelineItemQuickEditFieldsConfig?: PipelineItemQuickEditFieldsConfig
   isDisabled?: boolean
   isLinkedFieldsEnabled?: boolean
   hasPermission?: boolean
   isRemarksAttachmentsEnabled?: boolean
   visibilityFieldId?: string
   isOtherFieldsEnabled?: boolean
   isRemarksEnabled?: boolean
   isSchedulingEnabled?: boolean
   captureSubmittedLocation?: boolean
   viewStyle?: ViewStyleEnum
}

export interface UserPropertiesTemplateType extends Template {}

export type LeadTemplatesStore = TemplateStore<
   LeadTemplateModel,
   LeadTemplateType
>

export type CustomObjectTemplateStore = TemplateStore<
   CustomObjectTemplateModel,
   CustomObjectTemplateType
>

export type ApplicationTemplateStore = TemplateStore<
   ApplicationTemplateModel,
   ApplicationTemplateType
>

export type DealTemplatesStore = TemplateStore<
   DealTemplateModel,
   DealTemplateType
>

export type ContactTemplatesStore = TemplateStore<
   ContactTemplateModel,
   ContactTemplateType
>

export type PipelineEventTemplateStore = TemplateStore<
   PipelineEventTemplateModel,
   PipelineEventTemplateType
>

export type TaskTemplatesStore = TemplateStore<
   TaskTemplateModel,
   TaskTemplateType
>
export type ActivityTemplatesStore = TemplateStore<
   ActivityTemplateModel,
   ActivityTemplateType
>

export type UserPropertiesTemplateStore = TemplateStore<
   UserPropertiesTemplateModel,
   UserPropertiesTemplateType
>

export interface List {
   listId: string
   name: string
   filterSetId?: string
   isOwner?: boolean
}

export interface Board {
   boardId: string
   name: string
   defaultFieldIds: Array<string>
   description?: string | null
   displayName: string
   filterSetId?: string
   parentId: string
   parentType: BoardParentType
   pipelineEntityType: PipelineEntityTypeEnum
   lists?: List[]
   isOwner?: boolean
   boardType: BoardTypeEnum
   createdBy?: UserModel | null
   hasPipelineItemCreationAccess?: boolean
}

export interface Group {
   groupId: string
   name: string
   profilePicUrl?: string | null
}

export interface GOFGroupConfigType {
   name: string
   fieldIds: string[]
}

export interface GOF {
   gofId: string
   orderedFieldIds: string[]
   name?: string | null
   description?: string | null
   gofCategory?: GOFCategory
   enableAddAnother?: boolean
   addAnotherGofResponsesLimit?: number | null
   sectionUiStyle?: SectionUiStyle
   groups?: GOFGroupConfigType[]
}

export type SectionUiStyle =
   | 'DEFAULT'
   | 'TABLE'
   | 'SETBACK_TABLE'
   | 'CUSTOM_TABLE'
   | 'MULTI_TABLE'

export interface GOFWithFields extends GOF {
   fields: UnionField[]
   sectionUiStyle?: SectionUiStyle
}

export interface Workflow {
   workflowId: string
   name?: string | null
}

export interface OverViewFieldsConfig {
   mobileCardOverviewFields: Array<string>
}

export enum ColorVariantsEnum {
   Blue = 'BLUE',
   BlueDark = 'BLUE_DARK',
   BlueLight = 'BLUE_LIGHT',
   Cyan = 'CYAN',
   Fuchsia = 'FUCHSIA',
   Green = 'GREEN',
   GreenLight = 'GREEN_LIGHT',
   Indigo = 'INDIGO',
   Moss = 'MOSS',
   Orange = 'ORANGE',
   OrangeDark = 'ORANGE_DARK',
   Pink = 'PINK',
   Purple = 'PURPLE',
   Rose = 'ROSE',
   Teal = 'TEAL',
   Violet = 'VIOLET',
   Yellow = 'YELLOW'
}

export enum StageCategory {
   Failed = 'FAILED',
   Progress = 'PROGRESS',
   Success = 'SUCCESS'
}

export interface StageTransitionCheckpoint {
   checkpointId: string
   description: string
   stageTransitionId: string
}

export interface StageTransitionCheckpointsConfig {
   stageTransitionId: string
   checkpointsHeaderText: string
   checkpoints: StageTransitionCheckpoint[]
}

export interface StageTransitionConfigRemarksConfig {
   enableRemarks: boolean
   isRemarksRequired: boolean
}

export interface StageTransitionConfirmationPopupConfig {
   enableConfirmationPopup: boolean
   title?: string
   description?: string
}

export interface StageTransition {
   stageTransitionId: string
   fromStageId: string
   toStageId: string
   actionName: string
   checkpointsConfig: StageTransitionCheckpointsConfig
   remarksConfig: StageTransitionConfigRemarksConfig
   isFeeCreationMandatory: boolean
   isNotesRequired?: boolean
   confirmationPopupConfig?: StageTransitionConfirmationPopupConfig
   transitionFieldIds: [string]
   order: number
   hasMandatoryTasks: boolean
   iconId?: string
   iconColor?: string
   toStage?: StageResponse
}

export interface StageWithBasicDetails {
   stageId: string
   name: string
   order: number
   color?: ColorVariantsEnum | null
   category?: StageCategory | null
}

export interface Stage extends StageWithBasicDetails {
   overviewFieldsConfig: OverViewFieldsConfig
   quickEditFields: string[]
   recommendedTaskTemplateIds: string[]
   editableFieldIds: string[]
   permittedGOFIds: string[]
   requiredFieldIds: string[]
   paymentPermissions?: FeePaymentPermissions
   stageTasksIdentifications?: StageTasksIdentifications[]
   completedLayoutTabIds?: string[]
   permittedLayoutTabIds?: string[]
   canMakeFieldsAsShortfall?: boolean
   removePermittedAddAnotherGOFIds?: Array<string>
   addPermittedAddAnotherGOFIds?: Array<string>
   hasNoteSheetAccess?: boolean
   hasStageDeletionStatus?: boolean
   pipelineId?: string | null
}

export interface WorkflowWithStages extends Workflow {
   stages: Stage[]
}

export interface Template {
   templateId: string
   name?: string | null
   gofs: GOFWithFields[]
   workflows: WorkflowWithStages[]
}

export interface GOFResponse {
   gofResponseId: string
   gofId: string
}

export interface GOFResponseWithFieldResponse extends GOFResponse {
   fieldResponses: UnionFieldResponse[]
}

export interface Record {
   recordId: string
   templateId: string
   gofResponses: GOFResponseWithFieldResponse[]
}

//TODO: not maintaining the workflow and stages here

export interface ValidationResponseType {
   errorMessage: string
   shouldShowError: boolean
}

export interface ViewItemType {
   viewId: string
   name: string
   type: ViewLayouts
}
export interface ViewsParentEntityDetails {
   entityId: string
   entityType: ViewsParentEntityType
}

//Filter Types
export type UnionConditionModel =
   | TextConditionModel
   | PhoneNumberConditionModel
   | NumberConditionModel
   | DateConditionModel
   | SelectorConditionModel
   | TimeConditionModel

export interface FilterModelInput {
   filterId: string
   filterName: string
   logicalOperator: LogicalOperatorEnum
   filterSetModel: FilterSetModel
}

export interface RangeValue<IValue> {
   from: IValue | null
   to: IValue | null
}

export type ConditionResponseType<BaseResponseType> =
   | BaseResponseType
   | BaseResponseType[]
   | RangeValue<BaseResponseType>
   | null

export type UnionConditionValueType = ConditionResponseType<
   string | Date | number | PhoneNumberResponse
>

export type UnionDateFieldModel =
   | DateFieldModel
   | DateTimeFieldModel
   | TimeFieldModel

export type UnionTimeFieldModel = TimeFieldModel
export type UnionDateFieldOperator =
   | typeof DATE_CONDITION_OPERATORS.EQ
   | typeof DATE_CONDITION_OPERATORS.NE
   | typeof DATE_CONDITION_OPERATORS.LT
   | typeof DATE_CONDITION_OPERATORS.LTE
   | typeof DATE_CONDITION_OPERATORS.GT
   | typeof DATE_CONDITION_OPERATORS.GTE
   | typeof DATE_CONDITION_OPERATORS.RANGE
   | typeof DATE_CONDITION_OPERATORS.EMPTY
   | typeof DATE_CONDITION_OPERATORS.NOT_EMPTY
   | typeof DATE_CONDITION_OPERATORS.IS_CHANGED
export type UnionTimeFieldOperator =
   | typeof TIME_CONDITION_OPERATORS.EQ
   | typeof TIME_CONDITION_OPERATORS.NE
   | typeof TIME_CONDITION_OPERATORS.LT
   | typeof TIME_CONDITION_OPERATORS.LTE
   | typeof TIME_CONDITION_OPERATORS.GT
   | typeof TIME_CONDITION_OPERATORS.GTE
   | typeof TIME_CONDITION_OPERATORS.RANGE
   | typeof TIME_CONDITION_OPERATORS.EMPTY
   | typeof TIME_CONDITION_OPERATORS.NOT_EMPTY
   | typeof TIME_CONDITION_OPERATORS.IS_CHANGED

export type DateOperatorWithResponse =
   | {
        operator: typeof DATE_CONDITION_OPERATORS.EQ
        response: Date | null
     }
   | { operator: typeof DATE_CONDITION_OPERATORS.NE; response: Date | null }
   | { operator: typeof DATE_CONDITION_OPERATORS.LT; response: Date }
   | { operator: typeof DATE_CONDITION_OPERATORS.LTE; response: Date }
   | { operator: typeof DATE_CONDITION_OPERATORS.GT; response: Date }
   | { operator: typeof DATE_CONDITION_OPERATORS.GTE; response: Date }
   | {
        operator: typeof DATE_CONDITION_OPERATORS.RANGE
        response: RangeValue<Date>
     }
   | { operator: typeof DATE_CONDITION_OPERATORS.EMPTY; response: null }
   | { operator: typeof DATE_CONDITION_OPERATORS.NOT_EMPTY; response: null }
   | { operator: typeof DATE_CONDITION_OPERATORS.IS_CHANGED; response: null }

export type TimeOperatorWithResponse =
   | {
        operator: typeof TIME_CONDITION_OPERATORS.EQ
        response: string | null
     }
   | { operator: typeof TIME_CONDITION_OPERATORS.NE; response: string | null }
   | { operator: typeof TIME_CONDITION_OPERATORS.LT; response: string }
   | { operator: typeof TIME_CONDITION_OPERATORS.LTE; response: string }
   | { operator: typeof TIME_CONDITION_OPERATORS.GT; response: string }
   | { operator: typeof TIME_CONDITION_OPERATORS.GTE; response: string }
   | {
        operator: typeof TIME_CONDITION_OPERATORS.RANGE
        response: RangeValue<string>
     }
   | { operator: typeof TIME_CONDITION_OPERATORS.EMPTY; response: null }
   | { operator: typeof TIME_CONDITION_OPERATORS.NOT_EMPTY; response: null }
   | { operator: typeof TIME_CONDITION_OPERATORS.IS_CHANGED; response: null }

export type UnionNumberFieldModel = IntegerFieldModel | FloatFieldModel
export type UnionNumberFieldOperator =
   | typeof NUMBER_CONDITION_OPERATORS.EQ
   | typeof NUMBER_CONDITION_OPERATORS.NE
   | typeof NUMBER_CONDITION_OPERATORS.LT
   | typeof NUMBER_CONDITION_OPERATORS.LTE
   | typeof NUMBER_CONDITION_OPERATORS.GT
   | typeof NUMBER_CONDITION_OPERATORS.GTE
   | typeof NUMBER_CONDITION_OPERATORS.RANGE
   | typeof NUMBER_CONDITION_OPERATORS.EMPTY
   | typeof NUMBER_CONDITION_OPERATORS.NOT_EMPTY
   | typeof NUMBER_CONDITION_OPERATORS.IS_CHANGED

export type NumberOperatorWithResponse =
   | {
        operator: typeof NUMBER_CONDITION_OPERATORS.EQ
        response: string | null
     }
   | { operator: typeof NUMBER_CONDITION_OPERATORS.NE; response: string | null }
   | { operator: typeof NUMBER_CONDITION_OPERATORS.LT; response: string }
   | { operator: typeof NUMBER_CONDITION_OPERATORS.LTE; response: string }
   | { operator: typeof NUMBER_CONDITION_OPERATORS.GT; response: string }
   | { operator: typeof NUMBER_CONDITION_OPERATORS.GTE; response: string }
   | {
        operator: typeof NUMBER_CONDITION_OPERATORS.RANGE
        response: RangeValue<string>
     }
   | { operator: typeof NUMBER_CONDITION_OPERATORS.EMPTY; response: null }
   | { operator: typeof NUMBER_CONDITION_OPERATORS.NOT_EMPTY; response: null }
   | { operator: typeof NUMBER_CONDITION_OPERATORS.IS_CHANGED; response: null }

export type UnionPhoneNumberFieldOperator =
   | typeof PHONE_NUMBER_CONDITION_OPERATORS.EQ
   | typeof PHONE_NUMBER_CONDITION_OPERATORS.NE
   | typeof PHONE_NUMBER_CONDITION_OPERATORS.EMPTY
   | typeof PHONE_NUMBER_CONDITION_OPERATORS.NOT_EMPTY
   | typeof PHONE_NUMBER_CONDITION_OPERATORS.IS_CHANGED

export type PhoneNumberOperatorWithResponse =
   | {
        operator: typeof PHONE_NUMBER_CONDITION_OPERATORS.EQ
        response: PhoneNumberResponse | null
     }
   | {
        operator: typeof PHONE_NUMBER_CONDITION_OPERATORS.NE
        response: PhoneNumberResponse | null
     }
   | { operator: typeof PHONE_NUMBER_CONDITION_OPERATORS.EMPTY; response: null }
   | {
        operator: typeof PHONE_NUMBER_CONDITION_OPERATORS.NOT_EMPTY
        response: null
     }
   | {
        operator: typeof PHONE_NUMBER_CONDITION_OPERATORS.IS_CHANGED
        response: null
     }

export type UnionSelectorFieldModel =
   | DropdownFieldModel
   | RadioGroupFieldModel
   | CheckboxGroupFieldModel
   | FieldSelectorFieldModel
   | MultiSelectFieldModel
   | SearchableFieldModel

export type UnionSelectorFieldOperator =
   | typeof SELECTOR_CONDITION_OPERATORS.IN
   | typeof SELECTOR_CONDITION_OPERATORS.NOT_IN
   | typeof SELECTOR_CONDITION_OPERATORS.EMPTY
   | typeof SELECTOR_CONDITION_OPERATORS.NOT_EMPTY
   | typeof SELECTOR_CONDITION_OPERATORS.IS_CHANGED

export type SelectorOperatorWithResponse =
   | { operator: typeof SELECTOR_CONDITION_OPERATORS.IN; response: string[] }
   | {
        operator: typeof SELECTOR_CONDITION_OPERATORS.NOT_IN
        response: string[]
     }
   | { operator: typeof SELECTOR_CONDITION_OPERATORS.EMPTY; response: null }
   | { operator: typeof SELECTOR_CONDITION_OPERATORS.NOT_EMPTY; response: null }
   | {
        operator: typeof SELECTOR_CONDITION_OPERATORS.IS_CHANGED
        response: null
     }

export type UnionTextFieldModel =
   | PlainTextFieldModel
   | EmailFieldModel
   | LongTextFieldModel
   | UrlFieldModel
export type UnionTextFieldOperator =
   | typeof TEXT_CONDITION_OPERATORS.EQ
   | typeof TEXT_CONDITION_OPERATORS.NE
   | typeof TEXT_CONDITION_OPERATORS.CONTAINS
   | typeof TEXT_CONDITION_OPERATORS.EMPTY
   | typeof TEXT_CONDITION_OPERATORS.NOT_EMPTY
   | typeof TEXT_CONDITION_OPERATORS.IS_CHANGED

export type TextOperatorWithResponse =
   | { operator: typeof TEXT_CONDITION_OPERATORS.EQ; response: string | null }
   | { operator: typeof TEXT_CONDITION_OPERATORS.NE; response: string | null }
   | {
        operator: typeof TEXT_CONDITION_OPERATORS.CONTAINS
        response: string | null
     }
   | { operator: typeof TEXT_CONDITION_OPERATORS.EMPTY; response: null }
   | { operator: typeof TEXT_CONDITION_OPERATORS.NOT_EMPTY; response: null }
   | { operator: typeof TEXT_CONDITION_OPERATORS.IS_CHANGED; response: null }

export interface SortResponseType {
   sortBy: string
   sortValue: SortValueType
   templateId?: string | null
}

// TODO: Need to fix the usage of `UnionConditionOperatorType`` type as this is giving `string` type
export type UnionConditionOperatorType =
   | typeof CONDITION_OPERATORS.EQ
   | typeof CONDITION_OPERATORS.NE
   | typeof CONDITION_OPERATORS.LT
   | typeof CONDITION_OPERATORS.LTE
   | typeof CONDITION_OPERATORS.GT
   | typeof CONDITION_OPERATORS.GTE
   | typeof CONDITION_OPERATORS.IN
   | typeof CONDITION_OPERATORS.NOT_IN
   | typeof CONDITION_OPERATORS.CONTAINS
   | typeof CONDITION_OPERATORS.RANGE
   | typeof CONDITION_OPERATORS.EMPTY
   | typeof CONDITION_OPERATORS.NOT_EMPTY
   | typeof CONDITION_OPERATORS.IS_CHANGED

export interface FieldInterface {
   fieldId: string
   fieldType: UnionFieldType
   name: string | undefined | null
   systemFieldEnum?: SystemFieldEnum
}

export interface ColumnInterface {
   columnId: string
   width?: number
   isWrapped?: boolean
}

export interface SectionTemplateInterface {
   templateId: string
   fields: FieldInterface[]
   name?: string | null
   templateDetailsStatus: TemplateDetailsStatusEnum
}

export interface FieldsSectionType {
   template: SectionTemplateInterface
   isExpanded?: boolean
}

export interface FieldsDataInterface {
   shownFields: FieldInterface[]
   frozenFields: FieldInterface[]
}

export interface GetLinkedGraphsForAViewRequestType {
   viewId: string
   limit: number
   offset: number
   searchQuery: string
}

export interface ShareAGraphRequestType {
   graphId: string
   roles: Array<string>
}

export interface QuickFiltersType {
   filterSetModel: FilterSetModel
}

export interface GraphModelInputType {
   graphId: string
   name: string
   description?: string | null
   graphType: GraphTypeEnum
   pipelineId: string
   dataSources: AnalyticsDataSourceTypeEnum[]
   dataSourcesDisplayNames: string[]
   analyticsLevel: AnalyticsLevelEnum
   isOwner: boolean
   displayWidthInPercentage?: number
}

export interface BarGraphConfig {
   layout: BarGraphLayoutEnum
   dataLabelsType: GraphDataLabelsEnum
   numberOfBars: number
}

export interface DonutChartConfig {
   numberOfSlices: number
}

export interface LineGraphConfig {
   numberOfLines: number
   showPoints: boolean
   showZeroValues: boolean
}

export interface BarGraphModelInputType
   extends GraphModelInputType,
      BarGraphConfig {}

export interface DonutChartModelInputType
   extends GraphModelInputType,
      DonutChartConfig {}

export interface LineGraphModelInputType
   extends GraphModelInputType,
      LineGraphConfig {}

export interface ScorecardChartConfig {
   comparisonType: ScorecardComparisonTypeEnum
   comparisonValue: MetricModel | string | null
   advancedFilters: FilterSetModelV1
}

export interface ScorecardChartModelInputType
   extends GraphModelInputType,
      ScorecardChartConfig {}

export interface ScorecardChartDataType {
   metricName: string
   value: string
   displayValue: string
   comparisonDiffValue: string | null
   comparisonDiffDisplayValue: string | null
}

export interface GraphConfigType {
   quickFilters?: QuickFiltersModel
   advancedFilters: {
      filterSetModel: FilterSetModelV1
   }
}

export interface FunnelGraphConfigType extends GraphConfigType {
   enableLabels: boolean
   dropValueComparisonType: FunnelDropValueComparisonTypeEnum
   labelType: FunnelLabelTypeEnum
}

export interface FunnelGraphModelInputType
   extends GraphModelInputType,
      FunnelGraphConfigType {}

export interface FunnelGraphMetricValueType {
   metricId: string
   name: string
   value: string
   displayValue: string
}

export interface FunnelGraphDataType {
   metrics: FunnelGraphMetricValueType[]
}

export interface TableGraphConfigType extends GraphConfigType {
   enableRowTotals: boolean
   enableColumnTotals: boolean
   metricFilters?: {
      filterSetModel: FilterSetModelV1 | null
   }
}

export interface GraphBaseDetailsType
   extends GraphModelInputType,
      TableGraphConfigType {}

export interface AnalyticsField {
   fieldId: string
   name: string
   fieldType: UnionFieldType
   dataSource: AnalyticsDataSourceTypeEnum
   systemFieldEnum?: SystemFieldEnum | null
}
export interface DimensionInputType {
   field: AnalyticsField
   name: string
   histogramInterval: string | null
   graphDimensionId: string
   sortValue: SortValueEnum | null
   fieldDataSource: AnalyticsDataSourceTypeEnum
}

export interface MetricInputType {
   metricId: string
   metricType: MetricTypeEnum
   field?: AnalyticsField
   name: string
   isHidden: boolean
   filterSetModel: FilterSetModelV1
   formulaNodes?: FormulaNodeType[]
}

export enum MetricTypeEnum {
   Sum = 'SUM',
   Avg = 'AVG',
   Max = 'MAX',
   Min = 'MIN',
   UniqueCount = 'UNIQUE_COUNT',
   Count = 'COUNT',
   CustomMetric = 'CUSTOM_METRIC'
}

export interface FormulaNodeType {
   nodeType: FormulaNodeEnum
   value: string
}

export enum FormulaNodeEnum {
   Brace = 'BRACE',
   Constant = 'CONSTANT',
   Metric = 'METRIC',
   Operator = 'OPERATOR'
}

export interface SalesPipelineType {
   salesPipelineId: string
   name: string
}

export interface RoleModelInputType {
   roleId: string
   name: string
}

export interface PivotTableGraph extends GraphBaseDetailsType {}

export interface ShareAGraphResponseType {
   message: string
}

export interface GetPivotTableDataRequestType {
   graphId: string
   limit: number
   offset: number
}

export interface TableColumnHeaderType {
   columnId: string
   displayValue: string | ProfileHeader
}

export interface PivotTableColumnDataType {
   columnId: string
   value: number | ProfileHeader
}

export interface PivotTableRowDataType {
   rowId: string
   columns: PivotTableColumnDataType[]
}

export interface PivotTableDataType {
   row: PivotTableRowDataType
}

//NOTE: Get rows in pivot table types

export interface GetRowsInPivotTableResponseType {
   pivotTableData: PivotTableDataType[]
}
export interface GetPivotTableInitialDataResponseType {
   tableColumnHeaders: TableColumnHeaderType[]
   pivotTableData: PivotTableDataType[]
   totalRows: number
}

export interface ProfileHeader {
   profileUrl: string
   name: string
   attendance?: UserAttendance | null
}

export interface RowDataModel {
   rowId: string
   value: string | ProfileHeader
}

export interface PivotTableRowModelInputType {
   rowId: string
}
export interface GetPivotTableGraphRequestType {
   graphId: string
}

export interface ColumnTotalType {
   columnId: string
   total: string
}

export interface RowTotalType {
   rowId: string
   total: string
}

export type ColorVariantEnum =
   | 'BLUE'
   | 'BLUE_DARK'
   | 'BLUE_LIGHT'
   | 'CYAN'
   | 'FUCHSIA'
   | 'GREEN'
   | 'GREEN_LIGHT'
   | 'INDIGO'
   | 'MOSS'
   | 'ORANGE'
   | 'ORANGE_DARK'
   | 'PINK'
   | 'PURPLE'
   | 'ROSE'
   | 'TEAL'
   | 'VIOLET'
   | 'YELLOW'
   | 'PRIMARY'
   | 'GRAY'
   | 'ERROR'
   | 'SUCCESS'
   | 'WARNING'

export interface TaskCallStatusOptionsType {
   color: ColorVariantEnum
   option: string
}

export interface GraphDataStageType {
   stageId: string
   name: string
   color?: ColorVariantsEnum | null
}

export interface GraphDataUserType {
   userId: string
   name: string
   profilePicUrl: string
   attendance?: UserAttendance | null
}

export interface GraphDataTaskTemplateType {
   taskTemplateId: string
   name: string
}

export interface GraphDataActivityTemplateType {
   activityTemplateId: string
   name: string
}

export interface GraphDataType {
   name:
      | string
      | GraphDataStageType
      | GraphDataUserType
      | GraphDataTaskTemplateType
      | GraphDataActivityTemplateType
   value: string
   displayValue: string
   percentage: string
}

export type UnionGraphModelType =
   | PivotTableGraphModel
   | TableGraphModel
   | BarGraphModel
   | DonutGraphModel
   | ScorecardChartModel
   | LineGraphModel
   | FunnelGraphModel

export interface BarGraphDataType {
   xAxisName: string
   yAxisName: string
   data: GraphDataType[]
   averageValue: string
   averageDisplayValue: string
}

export interface DonutGraphDataType {
   data: GraphDataType[]
   totalValue: string
   dimensionName: string
   metricName: string
}

export type UnionShareWithType = UserModel | GroupModel

export interface QuickFilterFieldType {
   quickFilterFieldId: string
   field: AnalyticsField
}

interface LineLabelType {
   lineId: string
   displayName: string
}

interface YAxisDataPointType {
   dataPointId: string
   value: string
   displayName: string
}

interface LineGraphData {
   xAxisId: string
   xAxisValue: string
   yAxisDataPoints: YAxisDataPointType[]
}

export interface LineGraphDataType {
   graphId: string
   lineLabels: LineLabelType[]
   data: LineGraphData[]
   xAxisName: string
   yAxisName: string
}
export interface TableSortConfigType {
   columnId: string
   sortValue: SortValueEnum
}

export interface PipelineIcon {
   iconId?: string
   iconColor?: string
}

export enum ViewStyleEnum {
   SidePeek = 'SIDE_PEEK',
   Popup = 'POPUP'
}

export enum PipelineStageTransitionStyleEnum {
   Buttons = 'BUTTONS',
   Dropdown = 'DROPDOWN'
}

export type EntityType =
   | 'CONTACT'
   | 'LEAD'
   | 'DEAL'
   | 'CUSTOM_OBJECT'
   | 'APPLICATION'

export type CRMRecordProxyFieldResponseType = Omit<
   UnionFieldResponseWithField,
   'fieldType' | 'fieldId'
>

export type RecordActionType = 'DELETE' | 'RESTORE'

export enum ApplicationCustomTabsEnum {
   Blockchain = 'BLOCKCHAIN',
   Documents = 'DOCUMENTS',
   Payments = 'PAYMENTS',
   Scrutiny = 'SCRUTINY',
   ProposalFlow = 'PROPOSAL_FLOW',
   Relation = 'RELATION',
   ShortfallLogs = 'SHORTFALL_LOGS'
}

export enum ApplicationSurveyNumberRemarksEnum {
   Prohibited = 'PROHIBITED'
}

export type ObservationType = 'ACCEPTED' | 'REJECTED' | 'SHORTFALL'

export type TDRRequestType = 'DEFAULT' | 'OC' | 'ADD_REV'

export interface TDRRequestTabConfig {
   isTdrCurrentMarketValueEnabled?: boolean
   isProposedSiteDetailsEnabled?: boolean
   isProposedSiteToTDRValidationEnabled?: boolean
   proposedSiteCurrentMarketFieldId?: string
   proposedSiteAreaFieldId?: string
   caseTypeFieldId?: string
   tabType: TDRRequestType
}

export interface ApplicationAppealConfig {
   appealId: string
   status: AppealStatusEnum
}

export interface FieldIdWithGofResponseId {
   fieldId: string
   gofResponseId?: string | null
}

export enum ErrorTypeEnum {
   REQUIRED_FIELDS = 'REQUIRED_FIELDS',
   UNVERIFIED_FIELDS = 'UNVERIFIED_FIELDS',
   INVALID = 'INVALID'
}

export interface ErrorField extends FieldIdWithGofResponseId {
   locationId?: string | null
   errorType: ErrorTypeEnum
}

export interface ErrorFieldsConfig {
   errorFields: ErrorField[]
   hasErrorForField: (fieldId: string, gofResponseId?: string | null) => boolean
   removeErrorForField: (fieldId: string, gofResponseId?: string | null) => void
}
